import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  People as PeopleIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Description as TemplateIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  // Estados para dados reais
  const [stats, setStats] = React.useState({
    totalUsers: 0,
    activeSignatures: 0,
    pendingSync: 0,
    totalTemplates: 0,
    activeSchedulers: 0
  });
  const [recentActivities, setRecentActivities] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Carregar dados do dashboard
  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Carregar estatísticas
      const token = localStorage.getItem('authToken');
      const statsResponse = await fetch('/api/dashboard/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      } else {
        console.error('Erro ao carregar stats:', statsResponse.status);
      }

      // Carregar atividades recentes
      const activitiesResponse = await fetch('/api/dashboard/activities', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (activitiesResponse.ok) {
        const activitiesData = await activitiesResponse.json();
        setRecentActivities(activitiesData);
      } else {
        console.error('Erro ao carregar atividades:', activitiesResponse.status);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      setError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados ao montar o componente
  React.useEffect(() => {
    loadDashboardData();
  }, []);

  // Função para formatar tempo relativo
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Agora mesmo';
    if (diffInMinutes < 60) return `${diffInMinutes} min atrás`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hora${diffInHours > 1 ? 's' : ''} atrás`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} dia${diffInDays > 1 ? 's' : ''} atrás`;
  };

  // Função para obter cor do status
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'warning': return 'warning';
      default: return 'default';
    }
  };

  // Função para obter ícone do status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircleIcon fontSize="small" />;
      case 'error': return <ErrorIcon fontSize="small" />;
      case 'warning': return <WarningIcon fontSize="small" />;
      default: return <CheckCircleIcon fontSize="small" />;
    }
  };

  const StatCard: React.FC<{ 
    title: string; 
    value: number; 
    icon: React.ReactNode; 
    color: string;
    subtitle?: string;
    trend?: { value: number; positive: boolean };
  }> = ({ title, value, icon, color, subtitle, trend }) => (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}20`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box sx={{ color: color }}>
            {icon}
          </Box>
          {trend && (
            <Chip
              label={`${trend.positive ? '+' : ''}${trend.value}%`}
              size="small"
              color={trend.positive ? 'success' : 'error'}
              sx={{ fontSize: '0.75rem' }}
            />
          )}
        </Box>
        <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 1, color: color }}>
          {value.toLocaleString()}
        </Typography>
        <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const QuickActionCard: React.FC<{ 
    title: string; 
    description: string; 
    icon: React.ReactNode; 
    onClick: () => void;
    color: string;
  }> = ({ title, description, icon, onClick, color }) => (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>
            {icon}
          </Avatar>
          <Box flex={1}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh', 
      bgcolor: 'background.default', 
      py: 2
    }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              Dashboard
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Bem-vindo(a) de volta, {user?.name}! Aqui está o resumo do UniSign.
            </Typography>
          </Box>
          <Tooltip title="Atualizar dados">
            <IconButton 
              onClick={loadDashboardData}
              disabled={loading}
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      ) : error ? (
        <Box sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        </Box>
      ) : (
        <>
          {/* Cards de Estatísticas */}
          <Grid container spacing={3} sx={{ mb: 4 }} columns={{ xs: 12, sm: 12, lg: 20 }}>
            <Grid item xs={12} sm={6} lg={4}>
              <StatCard
                title="Total de Usuários"
                value={stats.totalUsers}
                icon={<PeopleIcon sx={{ fontSize: 32 }} />}
                color="#2563eb"
                subtitle="Usuários cadastrados"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <StatCard
                title="Assinaturas Ativas"
                value={stats.activeSignatures}
                icon={<EmailIcon sx={{ fontSize: 32 }} />}
                color="#10b981"
                subtitle="Assinaturas funcionando"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <StatCard
                title="Pendentes"
                value={stats.pendingSync}
                icon={<WarningIcon sx={{ fontSize: 32 }} />}
                color="#f59e0b"
                subtitle="Aguardando sincronização"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <StatCard
                title="Templates"
                value={stats.totalTemplates}
                icon={<TemplateIcon sx={{ fontSize: 32 }} />}
                color="#06b6d4"
                subtitle="Templates disponíveis"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <StatCard
                title="Agendadores"
                value={stats.activeSchedulers}
                icon={<ScheduleIcon sx={{ fontSize: 32 }} />}
                color="#7c3aed"
                subtitle="Agendadores ativos"
              />
            </Grid>
          </Grid>

          {/* Conteúdo Principal */}
          <Grid container spacing={4}>
            {/* Atividades Recentes */}
            <Grid item xs={12} lg={8}>
              <Paper sx={{ p: 3, height: 'fit-content' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Atividades Recentes
                  </Typography>
                  <Chip label={`${recentActivities.length} atividades`} size="small" />
                </Box>
                
                {recentActivities.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography color="text.secondary" variant="h6">
                      Nenhuma atividade recente
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      As atividades aparecerão aqui quando houver movimentação no sistema.
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {recentActivities.map((activity, index) => (
                      <React.Fragment key={activity.id}>
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Avatar 
                              sx={{ 
                                width: 32, 
                                height: 32, 
                                bgcolor: `${getStatusColor(activity.status)}.main` 
                              }}
                            >
                              {getStatusIcon(activity.status)}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {activity.action}
                              </Typography>
                            }
                            secondary={
                              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                <Typography variant="body2" color="text.secondary">
                                  {activity.user}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  •
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {formatRelativeTime(activity.time)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < recentActivities.length - 1 && (
                          <Divider sx={{ mx: 4 }} />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>

            {/* Ações Rápidas */}
            <Grid item xs={12} lg={4}>
              <Paper sx={{ p: 3, height: 'fit-content' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                  Ações Rápidas
                </Typography>
                
                <Box display="flex" flexDirection="column" gap={2}>
                  <QuickActionCard
                    title="Sincronizar Assinaturas"
                    description="Sincronizar todas as assinaturas do sistema"
                    icon={<TrendingUpIcon />}
                    onClick={() => window.location.href = '/users'}
                    color="#10b981"
                  />
                  
                  <QuickActionCard
                    title="Gerenciar Usuários"
                    description="Visualizar e editar usuários"
                    icon={<PeopleIcon />}
                    onClick={() => window.location.href = '/users'}
                    color="#2563eb"
                  />
                  
                  <QuickActionCard
                    title="Criar Template"
                    description="Criar novo template de assinatura"
                    icon={<AddIcon />}
                    onClick={() => window.location.href = '/templates'}
                    color="#7c3aed"
                  />
                  
                  <QuickActionCard
                    title="Configurações"
                    description="Acessar configurações do sistema"
                    icon={<SettingsIcon />}
                    onClick={() => window.location.href = '/schedulers'}
                    color="#06b6d4"
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
